import styled from "@emotion/styled";
import { useRef, useState } from "react";
import { Dialog } from "react-aria-components";

import {
  CommercialMeterKey,
  CommercialPlanKey,
  formatCommercialPrice,
  type CommercialPlanDefinition,
} from "@/modules/commercial-plans";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSPopover,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

const COMPARISON_METERS: readonly { key: CommercialMeterKey; label: string }[] = [
  { key: CommercialMeterKey.UserRequests, label: "Messages to Mem" },
  { key: CommercialMeterKey.NewNotes, label: "New Notes per month" },
  { key: CommercialMeterKey.ActiveNotes, label: "Task and Project Tracking notes" },
  { key: CommercialMeterKey.OpenTasksProjects, label: "Open Tasks and Projects" },
  { key: CommercialMeterKey.ActiveCustomRoutines, label: "Custom Routines" },
  { key: CommercialMeterKey.RoutineExecutions, label: "Routine Executions" },
  { key: CommercialMeterKey.ActiveConnections, label: "Connections" },
  { key: CommercialMeterKey.VoiceModeHours, label: "Voice Mode hours" },
] as const;

interface PlanHighlight {
  description: string;
  helpLabel: string;
  key: CommercialMeterKey;
  label: string;
}

const formatAllowance = ({ key, value }: { key: CommercialMeterKey; value: number }): string => {
  if (key === CommercialMeterKey.VoiceModeHours) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)} hours`;
  }

  return new Intl.NumberFormat().format(value);
};

const getMeterDescription = ({
  key,
  plan,
}: {
  key: CommercialMeterKey;
  plan?: CommercialPlanDefinition;
}): string => {
  const activeNotes = plan?.allowances[CommercialMeterKey.ActiveNotes];
  const openTasksProjects = plan?.allowances[CommercialMeterKey.OpenTasksProjects];

  switch (key) {
    case CommercialMeterKey.UserRequests:
      return "A Message to Mem is any push-to-talk request, message in the floating chat, message through an external channel such as WhatsApp, or email sent to Mem.";
    case CommercialMeterKey.NewNotes:
      return "A New Note is any note created during the month.";
    case CommercialMeterKey.ActiveNotes:
      return activeNotes === undefined
        ? "Mem reviews the notes you create or edit during the month to detect tasks and projects automatically. Each plan sets how many notes are included."
        : `Mem reviews up to ${formatAllowance({ key, value: activeNotes })} notes you create or edit during the month to detect tasks and projects automatically.`;
    case CommercialMeterKey.OpenTasksProjects:
      return openTasksProjects === undefined
        ? "Mem tracks open tasks and active projects across the notes it reviews. Each plan sets how many can remain actively tracked at once."
        : `Mem tracks up to ${formatAllowance({ key, value: openTasksProjects })} open tasks and active projects at once across the notes it reviews.`;
    case CommercialMeterKey.ActiveCustomRoutines:
      return "A Custom Routine lets Mem prepare a briefing, do research, create or organize notes, and send reminders for you on a schedule. For example: “Send me a weekly meal plan note every Sunday.”";
    case CommercialMeterKey.RoutineExecutions:
      return "Each time a Custom Routine does its scheduled background work, it counts as one Routine Execution.";
    case CommercialMeterKey.ActiveConnections:
      return "A Connection links Mem to an external source such as Gmail, Slack, or Todoist. Mem can use it as context, detect tasks and projects, and understand when work has already been completed.";
    case CommercialMeterKey.VoiceModeHours:
      return "Turn on Voice Mode inside any note to capture a meeting or think out loud. Mem creates a structured transcript and organizes it into notes. This allowance is the total captured time.";
  }
};

const getPlanHighlights = (plan: CommercialPlanDefinition): PlanHighlight[] => {
  const routines = plan.allowances[CommercialMeterKey.ActiveCustomRoutines];
  const connections = plan.allowances[CommercialMeterKey.ActiveConnections];
  const highlights = [
    buildPlanHighlight({
      key: CommercialMeterKey.UserRequests,
      label: `${formatAllowance({ key: CommercialMeterKey.UserRequests, value: plan.allowances[CommercialMeterKey.UserRequests] })} Messages to Mem`,
      plan,
    }),
    buildPlanHighlight({
      key: CommercialMeterKey.NewNotes,
      label: `${formatAllowance({ key: CommercialMeterKey.NewNotes, value: plan.allowances[CommercialMeterKey.NewNotes] })} New Notes per month`,
      plan,
    }),
    buildPlanHighlight({
      key: CommercialMeterKey.ActiveNotes,
      label: `Task and Project Tracking for up to ${formatAllowance({ key: CommercialMeterKey.ActiveNotes, value: plan.allowances[CommercialMeterKey.ActiveNotes] })} notes`,
      plan,
    }),
    buildPlanHighlight({
      key: CommercialMeterKey.OpenTasksProjects,
      label: `${formatAllowance({ key: CommercialMeterKey.OpenTasksProjects, value: plan.allowances[CommercialMeterKey.OpenTasksProjects] })} Open Tasks and Projects`,
      plan,
    }),
  ] satisfies PlanHighlight[];

  if (routines > 0) {
    highlights.push(
      buildPlanHighlight({
        key: CommercialMeterKey.ActiveCustomRoutines,
        label: routines === 1 ? "One Custom Routine" : `${routines} Custom Routines`,
        plan,
      })
    );
  }

  if (connections > 0) {
    highlights.push(
      buildPlanHighlight({
        key: CommercialMeterKey.ActiveConnections,
        label: connections === 1 ? "One Connection" : `${connections} Connections`,
        plan,
      })
    );
  }

  highlights.push(
    buildPlanHighlight({
      key: CommercialMeterKey.VoiceModeHours,
      label: `${formatAllowance({ key: CommercialMeterKey.VoiceModeHours, value: plan.allowances[CommercialMeterKey.VoiceModeHours] })} Voice Mode`,
      plan,
    })
  );

  return highlights;
};

const buildPlanHighlight = ({
  key,
  label,
  plan,
}: {
  key: CommercialMeterKey;
  label: string;
  plan: CommercialPlanDefinition;
}): PlanHighlight => ({
  description: getMeterDescription({ key, plan }),
  helpLabel: COMPARISON_METERS.find(meter => meter.key === key)?.label ?? label,
  key,
  label,
});

interface CommercialPlanSelectorProps {
  actionIsLoading?: boolean;
  allowCurrentPlanAction?: boolean;
  compactCards?: boolean;
  currentPlanActionLabel?: string;
  getActionLabel?: (plan: CommercialPlanDefinition) => string;
  currentPlanKey: string;
  isPlanAvailable?: (plan: CommercialPlanDefinition) => boolean;
  memProTrialIsAvailable?: boolean;
  onChoosePlan: (plan: CommercialPlanDefinition) => void;
  onSecondaryAction?: () => void;
  onSelectPlan: (planKey: CommercialPlanKey) => void;
  onShowAllPlans?: () => void;
  plans: readonly CommercialPlanDefinition[];
  recommendedPlanKey: CommercialPlanKey;
  selectedPlanKey: CommercialPlanKey;
  secondaryActionLabel?: string;
  showComparisonToggle?: boolean;
}

const getSelectionStateLabel = ({
  isCurrent,
  isSelected,
  isTrialOffer,
  plan,
}: {
  isCurrent: boolean;
  isSelected: boolean;
  isTrialOffer: boolean;
  plan: CommercialPlanDefinition;
}): string => {
  if (isCurrent) {
    return "Your plan";
  }

  if (isTrialOffer) {
    return isSelected ? "7-day free trial selected" : "Try Pro free";
  }

  return isSelected ? "Selected" : `Select ${plan.shortName}`;
};

export const CommercialPlanSelector = ({
  actionIsLoading = false,
  allowCurrentPlanAction = false,
  compactCards = false,
  currentPlanActionLabel = "Current plan",
  currentPlanKey,
  getActionLabel = plan => `Choose ${plan.displayName}`,
  isPlanAvailable = plan => plan.checkoutAvailable,
  memProTrialIsAvailable = false,
  onChoosePlan,
  onSecondaryAction,
  onSelectPlan,
  onShowAllPlans,
  plans,
  recommendedPlanKey,
  selectedPlanKey,
  secondaryActionLabel,
  showComparisonToggle = false,
}: CommercialPlanSelectorProps) => {
  const [showFullComparison, setShowFullComparison] = useState(false);
  const selectedPlan = plans.find(plan => plan.key === selectedPlanKey) ?? plans[0];
  const isMemProTrialOffer = (plan: CommercialPlanDefinition): boolean =>
    memProTrialIsAvailable && plan.key === CommercialPlanKey.MemPro && isPlanAvailable(plan);
  const getResolvedActionLabel = (plan: CommercialPlanDefinition): string =>
    isMemProTrialOffer(plan) ? "Start 7-day free trial" : getActionLabel(plan);

  return (
    <SelectorSurface>
      <PlanGrid $compact={compactCards} role="radiogroup" aria-label="Choose a Mem plan">
        {plans.map(plan => {
          const isCurrent = plan.key === currentPlanKey;
          const isAvailable = isCurrent || isPlanAvailable(plan);
          const isRecommended = plan.key === recommendedPlanKey && !isCurrent;
          const isSelected = plan.key === selectedPlan?.key;
          const isTrialOffer = isMemProTrialOffer(plan);

          return (
            <PlanCard
              key={plan.key}
              $compact={compactCards}
              $isCurrent={isCurrent}
              $isInteractive={!isCurrent && isAvailable}
              $isRecommended={isRecommended}
              $isSelected={isSelected}
            >
              <PlanCardChoice
                type="button"
                aria-checked={isSelected}
                aria-disabled={isCurrent || !isAvailable}
                aria-label={`${plan.displayName}, ${formatCommercialPrice(plan.monthlyPriceCents)} per month`}
                disabled={isCurrent || !isAvailable}
                role="radio"
                onClick={() => onSelectPlan(plan.key)}
              />

              <PlanHeader>
                <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
                  {plan.displayName}
                </MDSText>
                {isCurrent ? (
                  <MDSBadge label="Current" variant={MDSBadgeVariant.Default} />
                ) : !isAvailable ? (
                  <MDSBadge label="Coming soon" variant={MDSBadgeVariant.Default} />
                ) : isRecommended ? (
                  <MDSBadge label="Recommended" variant={MDSBadgeVariant.Emphasis} />
                ) : null}
              </PlanHeader>

              <PlanPrice>
                <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
                  {formatCommercialPrice(plan.monthlyPriceCents)}
                </MDSText>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  / month
                </MDSText>
              </PlanPrice>

              {isTrialOffer ? (
                <TrialOffer>
                  <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
                    Free for 7 days
                  </MDSText>
                  <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                    Then {formatCommercialPrice(plan.monthlyPriceCents)}/month. Cancel anytime.
                  </MDSText>
                </TrialOffer>
              ) : null}

              <PlanFeatures>
                {getPlanHighlights(plan).map(highlight => (
                  <PlanFeature key={highlight.key}>
                    <MDSIcon aria-hidden="true" kind={MDSIconKind.Check} size={13} />
                    <PlanFeatureLabel>
                      <MDSText variant={MDSTextVariant.TextS}>{highlight.label}</MDSText>
                      <MetricHelp description={highlight.description} label={highlight.helpLabel} />
                    </PlanFeatureLabel>
                  </PlanFeature>
                ))}
              </PlanFeatures>

              <SelectionState $isSelected={isSelected}>
                {getSelectionStateLabel({ isCurrent, isSelected, isTrialOffer, plan })}
              </SelectionState>
            </PlanCard>
          );
        })}
      </PlanGrid>

      <SelectorActions>
        {onSecondaryAction && secondaryActionLabel ? (
          <MDSButton onClick={onSecondaryAction}>{secondaryActionLabel}</MDSButton>
        ) : null}
        {onShowAllPlans ? <MDSButton onClick={onShowAllPlans}>See all plans</MDSButton> : null}
        {showComparisonToggle ? (
          <MDSButton onClick={() => setShowFullComparison(current => !current)}>
            {showFullComparison ? "Hide full comparison" : "Show full comparison"}
          </MDSButton>
        ) : null}
        <MDSButton
          variant={MDSButtonVariant.Emphasis}
          isDisabled={
            selectedPlan === undefined ||
            !isPlanAvailable(selectedPlan) ||
            (selectedPlan.key === currentPlanKey && !allowCurrentPlanAction)
          }
          isLoading={actionIsLoading}
          onClick={() => selectedPlan && onChoosePlan(selectedPlan)}
        >
          {selectedPlan?.key === currentPlanKey
            ? currentPlanActionLabel
            : selectedPlan && !isPlanAvailable(selectedPlan)
              ? "Coming soon"
              : selectedPlan
                ? getResolvedActionLabel(selectedPlan)
                : "Choose a plan"}
        </MDSButton>
      </SelectorActions>

      {showFullComparison ? (
        <ComparisonScroller>
          <ComparisonTable aria-label="Full plan comparison">
            <thead>
              <tr>
                <th>Included</th>
                {plans.map(plan => (
                  <th key={plan.key}>{plan.shortName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_METERS.map(({ key, label }) => (
                <tr key={key}>
                  <th>
                    <ComparisonMetricLabel>
                      {label}
                      <MetricHelp description={getMeterDescription({ key })} label={label} />
                    </ComparisonMetricLabel>
                  </th>
                  {plans.map(plan => (
                    <td key={plan.key}>{formatAllowance({ key, value: plan.allowances[key] })}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </ComparisonTable>
        </ComparisonScroller>
      ) : null}
    </SelectorSurface>
  );
};

const MetricHelp = ({ description, label }: { description: string; label: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <MetricHelpControl>
      <MetricHelpButton
        ref={triggerRef}
        aria-label={`Learn about ${label}`}
        aria-expanded={isOpen}
        iconKind={MDSIconKind.Question}
        size={MDSIconButtonSize.XSmall}
        type="button"
        variant={MDSIconButtonVariant.OnChrome}
        onPress={() => setIsOpen(current => !current)}
      />
      <MetricHelpPopover
        isNonModal
        isOpen={isOpen}
        placement="top"
        triggerRef={triggerRef}
        onOpenChange={setIsOpen}
      >
        <MetricHelpDialog aria-label={`${label} details`}>
          <MDSText variant={MDSTextVariant.TextS}>{description}</MDSText>
        </MetricHelpDialog>
      </MetricHelpPopover>
    </MetricHelpControl>
  );
};

const SelectorSurface = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  min-width: 0;
`;

const PlanGrid = styled.div<{ $compact: boolean }>`
  align-items: stretch;
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(${({ $compact }) => ($compact ? "180px" : "220px")}, 100%), 1fr)
  );
  margin: calc(-1 * ${({ theme }) => theme.spacing[2]});
  overflow: visible;
  padding: ${({ theme }) => theme.spacing[2]};
`;

const PlanCard = styled.article<{
  $compact: boolean;
  $isCurrent: boolean;
  $isInteractive: boolean;
  $isRecommended: boolean;
  $isSelected: boolean;
}>`
  background: ${({ theme, $isCurrent, $isRecommended, $isSelected }) => {
    if ($isCurrent) return theme.colors.background.card.default;
    if ($isRecommended || $isSelected) return theme.colors.background.input.chrome.focused;
    return theme.colors.background.input.chrome.default;
  }};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: ${({ theme, $isSelected }) => ($isSelected ? theme.shadows.surface : "none")};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  min-height: ${({ $compact }) => ($compact ? "300px" : "330px")};
  min-width: 0;
  opacity: ${({ $isCurrent }) => ($isCurrent ? 0.7 : 1)};
  position: relative;
  transform: translateY(0);
  transition:
    box-shadow 160ms ease,
    transform 160ms ease;

  ${({ $isInteractive, theme }) =>
    $isInteractive
      ? `
        &:hover {
          box-shadow: ${theme.shadows.surface};
          transform: translateY(-2px);
        }
      `
      : ""}
`;

const PlanCardChoice = styled.button`
  background: transparent;
  border: 0;
  border-radius: inherit;
  cursor: pointer;
  font: inherit;
  inset: 0;
  outline: none;
  padding: 0;
  position: absolute;
  z-index: 1;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

const PlanHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  justify-content: space-between;
  min-height: 32px;
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[5]} ${theme.spacing[3]}`};
  pointer-events: none;
  position: relative;
  z-index: 2;
`;

const PlanPrice = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: 0 ${({ theme }) => theme.spacing[5]};
  pointer-events: none;
  position: relative;
  z-index: 2;
`;

const TrialOffer = styled.div`
  background: ${({ theme }) => theme.colors.background.hoverOverlay.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]} 0`};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  pointer-events: none;
  position: relative;
  z-index: 2;
`;

const PlanFeatures = styled.ul`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  margin: 0;
  list-style: none;
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[5]}`};
  pointer-events: none;
  position: relative;
  z-index: 2;
`;

const PlanFeature = styled.li`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    flex: 0 0 auto;
    margin-top: 3px;
    color: ${({ theme }) => theme.colors.icon.emphasis};
  }
`;

const PlanFeatureLabel = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const MetricHelpControl = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  pointer-events: auto;
`;

const MetricHelpButton = styled(MDSIconButton)`
  background: ${({ theme }) => theme.colors.background.hoverOverlay.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  height: 18px;
  margin-top: 1px;
  width: 18px;
`;

const MetricHelpPopover = styled(MDSPopover)`
  max-width: min(340px, calc(100vw - 32px));
`;

const MetricHelpDialog = styled(Dialog)`
  outline: none;
`;

const SelectionState = styled.div<{ $isSelected: boolean }>`
  background: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.background.button.emphasis.default
      : theme.colors.background.button.default.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.text.onAccent : theme.colors.text.primary};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin: ${({ theme }) => `0 ${theme.spacing[4]} ${theme.spacing[4]}`};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  pointer-events: none;
  position: relative;
  text-align: center;
  z-index: 2;
`;

const SelectorActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ComparisonScroller = styled.div`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  max-width: 100%;
  overflow-x: auto;
`;

const ComparisonTable = styled.table`
  background: transparent;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 720px;
  width: 100%;

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
    text-align: left;
    white-space: nowrap;
  }

  thead th {
    background: ${({ theme }) => theme.colors.background.input.chrome.focused};
  }

  tbody th {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
  }

  tbody tr:nth-of-type(even) {
    background: ${({ theme }) => theme.colors.background.hoverOverlay.chrome};
  }
`;

const ComparisonMetricLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;
